<template>
  <div class="hms-page">
    <Navigation />

    <div class="container-fluid hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-hospital me-2"></i>{{ $t('page.ed_board', 'Emergency Department Board') }}</h4>
          <span class="hms-clock">{{ clockNow }}</span>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-blue" @click="$router.push('/triage')">
            <i class="mdi mdi-stethoscope me-1"></i>Triage Console
          </button>
          <button class="hms-btn hms-btn-amber" @click="autoRefresh = !autoRefresh">
            <i class="mdi" :class="autoRefresh ? 'mdi-pause' : 'mdi-play'"></i>
            {{ autoRefresh ? 'Pause' : 'Auto-refresh' }}
          </button>
          <button class="hms-btn hms-btn-blue" @click="load" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- MCI BANNER -->
      <div v-if="mci.mci_active == 1" class="hms-mci-banner mb-3">
        <i class="mdi mdi-alert-octagon"></i>
        <div class="flex-fill">
          <div class="hms-mci-title">MASS CASUALTY INCIDENT — {{ mci.mci_label || 'Active' }}</div>
          <div class="hms-mci-sub">{{ mci.live_mci_in_ed }} MCI casualties currently in ED</div>
        </div>
        <button class="hms-btn hms-btn-blue" @click="$router.push('/triage')"><i class="mdi mdi-stethoscope me-1"></i>Triage</button>
      </div>

      <!-- Stat row — acuity counts -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="filterAcuity = ''" :class="{ selected: !filterAcuity }">
          <div class="hms-stat-num">{{ stats.in_ed || 0 }}</div>
          <div class="hms-stat-lbl">In ED Now</div>
        </div>
        <div class="hms-stat hms-stat-l1" @click="filterAcuity='1_immediate'" :class="{ selected: filterAcuity==='1_immediate' }">
          <div class="hms-stat-num">{{ stats.lvl1 || 0 }}</div>
          <div class="hms-stat-lbl">L1 Immediate</div>
        </div>
        <div class="hms-stat hms-stat-l2" @click="filterAcuity='2_emergent'" :class="{ selected: filterAcuity==='2_emergent' }">
          <div class="hms-stat-num">{{ stats.lvl2 || 0 }}</div>
          <div class="hms-stat-lbl">L2 Emergent</div>
        </div>
        <div class="hms-stat hms-stat-l3" @click="filterAcuity='3_urgent'" :class="{ selected: filterAcuity==='3_urgent' }">
          <div class="hms-stat-num">{{ stats.lvl3 || 0 }}</div>
          <div class="hms-stat-lbl">L3 Urgent</div>
        </div>
        <div class="hms-stat hms-stat-l4" @click="filterAcuity='4_less_urgent'" :class="{ selected: filterAcuity==='4_less_urgent' }">
          <div class="hms-stat-num">{{ stats.lvl4 || 0 }}</div>
          <div class="hms-stat-lbl">L4 Less Urgent</div>
        </div>
        <div class="hms-stat hms-stat-l5" @click="filterAcuity='5_non_urgent'" :class="{ selected: filterAcuity==='5_non_urgent' }">
          <div class="hms-stat-num">{{ stats.lvl5 || 0 }}</div>
          <div class="hms-stat-lbl">L5 Non Urgent</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-text-success">{{ stats.discharged_today || 0 }}</div>
          <div class="hms-stat-lbl">Discharged Today</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-text-info">{{ stats.admitted_today || 0 }}</div>
          <div class="hms-stat-lbl">Admitted Today</div>
        </div>
      </div>

      <!-- Bay snapshot -->
      <div class="hms-bay-snapshot">
        <span class="hms-bay-pill" v-for="b in baySnapshot" :key="b.bay_type + '-' + b.status">
          {{ formatBayType(b.bay_type) }} <span class="hms-mono">{{ b.c }}</span> <em class="hms-bay-status" :class="'st-' + b.status">{{ b.status }}</em>
        </span>
      </div>

      <div v-if="loading && !visits.length" class="hms-spinner"><div class="spinner-border"></div></div>

      <!-- Patient cards grid -->
      <div v-else-if="filteredVisits.length" class="hms-ed-grid">
        <div v-for="v in filteredVisits" :key="v.visit_id"
             class="hms-ed-card"
             :class="['acu-' + (v.triage_level || 'unset'), { disposed: v.disposition }]"
             @click="openVisit(v)">

          <div class="hms-ed-head">
            <span class="hms-acuity" :class="'acu-' + (v.triage_level || 'unset')">{{ acuityShort(v.triage_level) }}</span>
            <span class="hms-ed-bay">
              <i class="mdi mdi-bed"></i>
              {{ v.bay_name || '— No bay —' }}
            </span>
            <span class="hms-ed-time" :class="overdueClass(v)" :title="'Time in ED'">
              <i class="mdi mdi-timer-outline"></i> {{ minutesIn(v.arrival_time) }}m
            </span>
          </div>

          <div class="hms-ed-body">
            <div class="hms-ed-name">
              <span v-if="v.is_unknown == 1" class="hms-badge hms-badge-warning me-1">UNKNOWN</span>
              {{ v.first_name }} {{ v.last_name }}
            </div>
            <div class="hms-muted-sm">
              <span class="hms-mono">{{ v.patient_no || '—' }}</span> · {{ ageStr(v.dob) }} · {{ v.gender || '—' }}
            </div>
            <div class="hms-ed-complaint" v-if="v.chief_complaint">"{{ truncate(v.chief_complaint, 90) }}"</div>
            <div v-if="v.allergies" class="hms-warn-strip"><i class="mdi mdi-alert-octagon"></i> Allergies: {{ truncate(v.allergies, 60) }}</div>
            <div v-if="v.chronic_conditions" class="hms-muted-sm"><i class="mdi mdi-pulse"></i> {{ truncate(v.chronic_conditions, 60) }}</div>
          </div>

          <div class="hms-ed-foot">
            <span class="hms-ed-vital" v-if="v.latest_news2 != null"><i class="mdi mdi-heart-pulse"></i> NEWS2: <strong>{{ v.latest_news2 }}</strong></span>
            <span class="hms-ed-vital" v-if="v.latest_pain != null"><i class="mdi mdi-emoticon-sad-outline"></i> Pain: <strong>{{ v.latest_pain }}</strong></span>
            <span class="hms-ed-vital" v-if="v.open_labs > 0"><i class="mdi mdi-flask"></i> {{ v.open_labs }}</span>
            <span class="hms-ed-vital" v-if="v.open_imaging > 0"><i class="mdi mdi-radioactive"></i> {{ v.open_imaging }}</span>
            <span class="hms-ed-vital" v-if="v.open_rx > 0"><i class="mdi mdi-pill"></i> {{ v.open_rx }}</span>
            <span class="hms-ed-doc ms-auto" v-if="v.doc_first">
              <i class="mdi mdi-doctor"></i> {{ v.doc_first }} {{ v.doc_last }}
            </span>
            <span v-if="v.disposition" class="hms-badge hms-badge-info ms-auto">{{ v.disposition }}</span>
          </div>
        </div>
      </div>
      <div v-else class="hms-empty">
        <i class="mdi mdi-hospital-marker"></i>{{ filterAcuity ? 'No patients at this acuity.' : 'ED is empty — no active patients.' }}
      </div>

      <!-- DETAIL DRAWER -->
      <div class="hms-modal-overlay" v-if="active" @click.self="active = null">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span class="d-flex align-items-center gap-2">
              <span class="hms-acuity hms-acuity-lg" :class="'acu-' + (active.triage_level || 'unset')">{{ acuityShort(active.triage_level) }}</span>
              <span>
                <span v-if="active.is_unknown == 1" class="hms-badge hms-badge-warning me-1">UNKNOWN</span>
                {{ active.first_name }} {{ active.last_name }}
                <div class="hms-muted-sm hms-mono">{{ active.patient_no }} · {{ ageStr(active.dob) }} · {{ active.gender }}</div>
              </span>
            </span>
            <button class="hms-modal-close" @click="active=null">&times;</button>
          </div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Visit</span><span class="hms-mono">{{ active.visit_number }}</span></div>
            <div><span class="hms-kv-key">Chief Complaint</span><span>{{ active.chief_complaint || '—' }}</span></div>
            <div><span class="hms-kv-key">Allergies</span><span>{{ active.allergies || '—' }}</span></div>
            <div><span class="hms-kv-key">Chronic</span><span>{{ active.chronic_conditions || '—' }}</span></div>
            <div><span class="hms-kv-key">Arrived</span><span>{{ formatDateTime(active.arrival_time) }} <span class="hms-muted-sm">({{ minutesIn(active.arrival_time) }}m ago)</span></span></div>
            <div v-if="active.triage_time"><span class="hms-kv-key">Triaged</span><span>{{ formatDateTime(active.triage_time) }} <span class="hms-muted-sm">(door-to-triage {{ delta(active.arrival_time, active.triage_time) }}m)</span></span></div>
            <div v-if="active.seen_by_doctor_time"><span class="hms-kv-key">Seen by Doctor</span><span>{{ formatDateTime(active.seen_by_doctor_time) }} <span class="hms-muted-sm">(door-to-doctor {{ delta(active.arrival_time, active.seen_by_doctor_time) }}m)</span></span></div>
            <div><span class="hms-kv-key">Bay</span><span>{{ active.bay_name || '— Not assigned —' }}</span></div>
            <div v-if="active.latest_news2 != null"><span class="hms-kv-key">Latest NEWS2</span><span><strong>{{ active.latest_news2 }}</strong> · Pain {{ active.latest_pain || 0 }}/10 <span class="hms-muted-sm">({{ relativeTime(active.last_vitals_at) }})</span></span></div>
            <div v-if="active.disposition"><span class="hms-kv-key">Disposition</span><span><span class="hms-badge hms-badge-info">{{ active.disposition }}</span></span></div>
          </div>

          <div v-if="!active.disposition">
            <div class="hms-section-label">QUICK ACTIONS</div>
            <div class="d-flex gap-2 mb-3 flex-wrap">
              <button class="hms-btn hms-btn-blue" @click="$router.push('/patients/' + active.patient_id + '/chart')">
                <i class="mdi mdi-folder-account-outline me-1"></i>Open Chart
              </button>
              <button class="hms-btn hms-btn-blue" @click="$router.push('/lab')"><i class="mdi mdi-flask me-1"></i>Lab</button>
              <button class="hms-btn hms-btn-blue" @click="$router.push('/radiology')"><i class="mdi mdi-radioactive me-1"></i>Radiology</button>
              <button class="hms-btn hms-btn-blue" @click="$router.push('/pharmacy')"><i class="mdi mdi-pill me-1"></i>Pharmacy</button>
              <button v-if="!active.seen_by_doctor_time" class="hms-btn hms-btn-amber" @click="markSeen(active)">
                <i class="mdi mdi-doctor me-1"></i>Mark Seen by Me
              </button>
            </div>

            <div class="hms-section-label">MOVE TO BAY</div>
            <div class="d-flex gap-2 align-items-center mb-3">
              <select class="hms-input flex-fill" v-model="moveBayId">
                <option value="">— Select bay —</option>
                <optgroup v-for="grp in availableBaysGrouped" :key="grp.type" :label="formatBayType(grp.type)">
                  <option v-for="b in grp.bays" :key="b.bay_id" :value="b.bay_id">{{ b.bay_name }}</option>
                </optgroup>
              </select>
              <button class="hms-btn hms-btn-primary" @click="doAssignBay" :disabled="!moveBayId || processing">Move</button>
            </div>

            <div class="hms-section-label">SET DISPOSITION</div>
            <div class="hms-disp-grid mb-3">
              <button v-for="d in dispositions" :key="d.code" class="hms-disp-btn" :class="d.cls" @click="openDisposition(d.code, d.label)">
                <i class="mdi" :class="d.icon"></i>
                <span>{{ d.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- DISPOSITION CONFIRM MODAL -->
      <div class="hms-modal-overlay" v-if="dispCode" @click.self="dispCode=null">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-check-decagram me-1"></i>Confirm: {{ dispLabel }}</span>
            <button class="hms-modal-close" @click="dispCode=null">&times;</button>
          </div>
          <div class="hms-info-banner mb-3" v-if="dispCode === 'admitted'">
            <i class="mdi mdi-information-outline me-1"></i>After confirming, go to <a href="javascript:void(0)" @click="$router.push('/wards')">Wards & Beds</a> to allocate a ward bed.
          </div>
          <div class="hms-info-banner mb-3" v-if="dispCode === 'theatre'">
            <i class="mdi mdi-information-outline me-1"></i>After confirming, go to <a href="javascript:void(0)" @click="$router.push('/theatre')">Theatre</a> to book the surgery.
          </div>
          <div class="hms-info-banner mb-3" v-if="dispCode === 'morgue'">
            <i class="mdi mdi-information-outline me-1"></i>After confirming, go to <a href="javascript:void(0)" @click="$router.push('/funeral-home')">Funeral Home</a> to admit the body.
          </div>
          <div class="row g-3 mb-3">
            <div class="col-12"><label class="hms-label">Notes</label>
              <textarea class="hms-input" rows="3" v-model="dispNotes" :placeholder="dispPlaceholder"></textarea>
            </div>
          </div>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-amber flex-fill" @click="dispCode=null">Cancel</button>
            <button class="hms-btn hms-btn-primary flex-fill" @click="confirmDisposition" :disabled="processing">
              <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>Confirm
            </button>
          </div>
        </div>
      </div>

      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
  --acu-1:#dc3545; --acu-2:#fd7e14; --acu-3:#ffc107; --acu-4:#28a745; --acu-5:#1a7fe8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
  --acu-1:#dc3545; --acu-2:#fd7e14; --acu-3:#ffc107; --acu-4:#28a745; --acu-5:#0047AB;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding: 20px 24px 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }
.hms-clock { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 14px; color: var(--hms-s); padding: 4px 10px; border: 1px solid var(--hms-br); border-radius: 8px; }

.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.hms-text-success { color: var(--hms-success); }
.hms-text-info { color: var(--hms-info); }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 14px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; flex: 1; min-width: 110px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { transform: translateY(-2px); border-color: var(--hms-primary); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; line-height: 1.1; color: var(--hms-h); }
.hms-stat-lbl { font-size: 11px; color: var(--hms-s); margin-top: 2px; }
.hms-stat-l1 { border-top: 4px solid var(--acu-1); }
.hms-stat-l2 { border-top: 4px solid var(--acu-2); }
.hms-stat-l3 { border-top: 4px solid var(--acu-3); }
.hms-stat-l4 { border-top: 4px solid var(--acu-4); }
.hms-stat-l5 { border-top: 4px solid var(--acu-5); }
.hms-stat-l1 .hms-stat-num { color: var(--acu-1); }
.hms-stat-l2 .hms-stat-num { color: var(--acu-2); }
.hms-stat-l3 .hms-stat-num { color: var(--acu-3); }
.hms-stat-l4 .hms-stat-num { color: var(--acu-4); }
.hms-stat-l5 .hms-stat-num { color: var(--acu-5); }

.hms-bay-snapshot { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 16px; }
.hms-bay-pill { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 50px; padding: 4px 12px; font-size: 12px; color: var(--hms-h); }
.hms-bay-status { font-style: normal; padding: 1px 6px; border-radius: 4px; margin-left: 4px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
.hms-bay-status.st-available     { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-bay-status.st-occupied      { background: rgba(220,53,69,.15); color: var(--hms-danger); }
.hms-bay-status.st-cleaning      { background: rgba(255,193,7,.15); color: var(--hms-warning); }
.hms-bay-status.st-out_of_service{ background: var(--hms-bg3); color: var(--hms-m); }

/* MCI banner */
.hms-mci-banner { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; background: linear-gradient(90deg, rgba(220,53,69,0.20), rgba(220,53,69,0.06)); border: 2px solid var(--hms-danger); border-radius: 12px; padding: 14px 18px; }
.hms-mci-banner > i { font-size: 32px; color: var(--hms-danger); flex-shrink: 0; }
.hms-mci-title { color: var(--hms-danger); font-weight: 800; font-size: 14px; letter-spacing: 1.5px; }
.hms-mci-sub   { color: var(--hms-h); font-size: 12px; margin-top: 2px; }

.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }

.hms-empty { text-align: center; padding: 60px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 64px; color: var(--hms-br); display: block; margin-bottom: 16px; }

/* Card grid */
.hms-ed-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 12px; }
.hms-ed-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-left: 6px solid var(--hms-br); border-radius: 12px; padding: 12px 14px; cursor: pointer; transition: all .15s; }
.hms-ed-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,.15); }
.hms-ed-card.acu-1_immediate    { border-left-color: var(--acu-1); }
.hms-ed-card.acu-2_emergent     { border-left-color: var(--acu-2); }
.hms-ed-card.acu-3_urgent       { border-left-color: var(--acu-3); }
.hms-ed-card.acu-4_less_urgent  { border-left-color: var(--acu-4); }
.hms-ed-card.acu-5_non_urgent   { border-left-color: var(--acu-5); }
.hms-ed-card.disposed { opacity: 0.55; }

.hms-ed-head { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.hms-ed-bay { color: var(--hms-h); font-size: 13px; font-weight: 600; }
.hms-ed-bay i { color: var(--hms-primary); }
.hms-ed-time { margin-left: auto; font-size: 12px; color: var(--hms-s); padding: 2px 8px; border: 1px solid var(--hms-br); border-radius: 50px; }
.hms-ed-time.overdue { color: var(--hms-danger); border-color: var(--hms-danger); background: rgba(220,53,69,.08); }
.hms-ed-time.warn    { color: var(--hms-warning); border-color: var(--hms-warning); }

.hms-acuity { display: inline-flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 8px; color: #fff; font-weight: 800; font-size: 13px; }
.hms-acuity.acu-1_immediate   { background: var(--acu-1); }
.hms-acuity.acu-2_emergent    { background: var(--acu-2); }
.hms-acuity.acu-3_urgent      { background: var(--acu-3); color: #1a1a1a; }
.hms-acuity.acu-4_less_urgent { background: var(--acu-4); }
.hms-acuity.acu-5_non_urgent  { background: var(--acu-5); }
.hms-acuity.acu-unset         { background: var(--hms-bg3); color: var(--hms-m); }
.hms-acuity-lg { width: 48px; height: 48px; font-size: 18px; }

.hms-ed-name { color: var(--hms-h); font-weight: 700; font-size: 15px; margin-top: 2px; }
.hms-ed-complaint { color: var(--hms-s); font-style: italic; font-size: 13px; margin-top: 6px; }
.hms-warn-strip { background: rgba(220,53,69,.08); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.25); border-radius: 6px; padding: 4px 8px; font-size: 12px; margin-top: 6px; }
.hms-warn-strip i { margin-right: 4px; }

.hms-ed-foot { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding-top: 8px; margin-top: 8px; border-top: 1px dashed var(--hms-br); }
.hms-ed-vital { font-size: 12px; color: var(--hms-h); display: inline-flex; align-items: center; gap: 4px; }
.hms-ed-vital i { color: var(--hms-primary); }
.hms-ed-doc { font-size: 12px; color: var(--hms-s); }

.hms-badge { padding: 2px 8px; border-radius: 50px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; }
.hms-badge-warning { background: rgba(255,193,7,.15); color: #ca8a04; }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }

/* Modal */
.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 760px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-sm { max-width: 480px; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin: 8px 0; }
.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 150px; font-weight: 600; }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }

.hms-disp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 8px; }
.hms-disp-btn { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 8px; cursor: pointer; transition: all .15s; display: flex; flex-direction: column; align-items: center; gap: 6px; color: var(--hms-h); font-size: 13px; font-weight: 600; }
.hms-disp-btn:hover { transform: translateY(-2px); border-color: currentColor; }
.hms-disp-btn i { font-size: 22px; }
.hms-disp-btn.disp-admitted    { color: var(--hms-info); }
.hms-disp-btn.disp-discharged  { color: var(--hms-success); }
.hms-disp-btn.disp-theatre     { color: #e74c3c; }
.hms-disp-btn.disp-referred_out{ color: #9b59b6; }
.hms-disp-btn.disp-morgue      { color: #7f8c8d; }
.hms-disp-btn.disp-observation { color: var(--hms-warning); }
.hms-disp-btn.disp-lwbs        { color: var(--hms-m); }
.hms-disp-btn.disp-dama        { color: var(--hms-danger); }
.hms-disp-btn.disp-transferred { color: var(--hms-primary); }

.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.hms-info-banner a { color: var(--hms-primary); font-weight: 700; text-decoration: underline; }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

const TARGET_MIN = { '1_immediate': 0, '2_emergent': 10, '3_urgent': 60, '4_less_urgent': 120, '5_non_urgent': 240 };

const DISPOSITIONS = [
  { code: 'admitted',     label: 'Admit',     icon: 'mdi-bed',                   cls: 'disp-admitted' },
  { code: 'discharged',   label: 'Discharge', icon: 'mdi-home-export-outline',   cls: 'disp-discharged' },
  { code: 'theatre',      label: 'Theatre',   icon: 'mdi-medical-bag',           cls: 'disp-theatre' },
  { code: 'observation',  label: 'Observe',   icon: 'mdi-clock-outline',         cls: 'disp-observation' },
  { code: 'referred_out', label: 'Refer Out', icon: 'mdi-truck-fast-outline',    cls: 'disp-referred_out' },
  { code: 'transferred',  label: 'Transfer',  icon: 'mdi-arrow-right-bold',      cls: 'disp-transferred' },
  { code: 'lwbs',         label: 'LWBS',      icon: 'mdi-walk',                  cls: 'disp-lwbs' },
  { code: 'dama',         label: 'DAMA',      icon: 'mdi-account-cancel-outline', cls: 'disp-dama' },
  { code: 'morgue',       label: 'Mortuary',  icon: 'mdi-grave-stone',           cls: 'disp-morgue' }
];

export default {
  name: "EdBoard",
  components: { Navigation },
  data() {
    return {
      loading: true, processing: false,
      autoRefresh: true, refreshTimer: null, clockTimer: null, clockNow: '',

      visits: [], stats: {}, baySnapshot: [], bays: [],
      mci: { mci_active: 0, mci_label: '', live_mci_in_ed: 0 },

      filterAcuity: '',

      active: null,

      moveBayId: '',

      dispCode: null, dispLabel: '', dispNotes: '',
      dispositions: DISPOSITIONS,

      toastMsg: "", toastError: false, toastTimer: null
    };
  },
  computed: {
    filteredVisits() {
      if (!this.filterAcuity) return this.visits;
      return this.visits.filter(v => v.triage_level === this.filterAcuity);
    },
    availableBaysGrouped() {
      var by = {};
      (this.bays || []).filter(b => b.status === 'available').forEach(b => {
        if (!by[b.bay_type]) by[b.bay_type] = [];
        by[b.bay_type].push(b);
      });
      return Object.keys(by).map(t => ({ type: t, bays: by[t] }));
    },
    dispPlaceholder() {
      if (this.dispCode === 'admitted')     return 'Working diagnosis, ward, admitting consultant...';
      if (this.dispCode === 'discharged')   return 'Discharge advice, follow-up plan...';
      if (this.dispCode === 'theatre')      return 'Procedure, urgency, consultant on call...';
      if (this.dispCode === 'referred_out') return 'Receiving facility, reason, mode of transport...';
      if (this.dispCode === 'morgue')       return 'Time of death, cause if known, family notified...';
      if (this.dispCode === 'lwbs')         return 'Time noticed missing, attempts to contact...';
      if (this.dispCode === 'dama')         return 'Reason cited, risks explained, signed?';
      return 'Notes...';
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.tickClock();
    this.clockTimer = setInterval(this.tickClock, 1000);
    this.load();
    this.refreshTimer = setInterval(() => { if (this.autoRefresh) this.load(true); }, 30000);
  },
  beforeUnmount() {
    if (this.clockTimer) clearInterval(this.clockTimer);
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    tickClock() {
      var d = new Date();
      this.clockNow = d.toLocaleTimeString();
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },

    async load(silent) {
      if (!silent) this.loading = true;
      try {
        var res = await Promise.all([
          executeJson({ function: "getEdBoard" }, hms),
          executeJson({ function: "getResusBays" }, hms),
          executeJson({ function: "getMciStatus" }, hms)
        ]);
        if (res[2].data && res[2].data.success) this.mci = (res[2].data.data && res[2].data.data.Mci) || this.mci;
        if (res[0].data && res[0].data.success) {
          this.visits      = (res[0].data.data && res[0].data.data.Visits)      || [];
          this.stats       = (res[0].data.data && res[0].data.data.Stats)       || {};
          this.baySnapshot = (res[0].data.data && res[0].data.data.Bays)        || [];
        }
        if (res[1].data && res[1].data.success) {
          this.bays = (res[1].data.data && res[1].data.data.Bays) || [];
        }
      } catch(e) { console.error(e); }
      this.loading = false;
    },

    openVisit(v) { this.active = v; this.moveBayId = ''; },

    acuityShort(l) { return ({ '1_immediate':'L1', '2_emergent':'L2', '3_urgent':'L3', '4_less_urgent':'L4', '5_non_urgent':'L5' })[l] || '?'; },
    formatBayType(t) { return ({ resus:'Resus', cubicle:'Cubicle', observation:'Obs', triage:'Triage', isolation:'Isolation' })[t] || t; },

    minutesIn(arrival) {
      if (!arrival) return 0;
      var d = new Date((arrival || '').replace ? arrival.replace(' ','T') : arrival);
      if (isNaN(d)) return 0;
      return Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
    },
    delta(a, b) {
      if (!a || !b) return '?';
      var da = new Date(a.replace ? a.replace(' ','T') : a);
      var db = new Date(b.replace ? b.replace(' ','T') : b);
      if (isNaN(da) || isNaN(db)) return '?';
      return Math.max(0, Math.floor((db - da) / 60000));
    },
    overdueClass(v) {
      var target = TARGET_MIN[v.triage_level];
      if (target == null) return '';
      var mins = this.minutesIn(v.arrival_time);
      if (mins > target * 1.5) return 'overdue';
      if (mins > target) return 'warn';
      return '';
    },
    ageStr(dob) {
      if (!dob || dob === '0000-00-00' || dob === '1900-01-01') return 'age unknown';
      var d = new Date(dob); if (isNaN(d)) return '—';
      var diff = Date.now() - d.getTime();
      var years = Math.floor(diff / (365.25 * 24 * 3600000));
      return years + 'y';
    },
    truncate(s, n) { return (s || '').length > n ? s.substring(0,n) + '...' : (s || ''); },
    formatDateTime(s) { if (!s) return '—'; var d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleString(); },
    relativeTime(s) {
      if (!s) return '—'; var d = new Date(s.replace ? s.replace(' ','T') : s); if (isNaN(d)) return s;
      var diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return Math.floor(diff) + 's ago';
      if (diff < 3600) return Math.floor(diff/60) + 'm ago';
      if (diff < 86400) return Math.floor(diff/3600) + 'h ago';
      return d.toLocaleDateString();
    },

    async markSeen(v) {
      try {
        var r = await executeJson({ function: "markSeenByDoctor", visit_id: v.visit_id }, hms);
        if (r.data && r.data.success) { this.showToast("Marked as seen"); await this.load(true); this.active = this.visits.find(x => x.visit_id === v.visit_id) || null; }
        else this.showToast(r.data.message || "Failed", true);
      } catch(e) { this.showToast("Connection error", true); }
    },

    async doAssignBay() {
      if (!this.moveBayId || !this.active) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "assignBay", bay_id: this.moveBayId, visit_id: this.active.visit_id }, hms);
        if (r.data && r.data.success) {
          this.showToast("Bay assigned");
          await this.load(true);
          this.active = this.visits.find(x => x.visit_id === this.active.visit_id) || null;
        } else this.showToast(r.data.message || "Failed", true);
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    openDisposition(code, label) { this.dispCode = code; this.dispLabel = label; this.dispNotes = ''; },
    async confirmDisposition() {
      if (!this.dispCode || !this.active) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "setDisposition", visit_id: this.active.visit_id, disposition: this.dispCode, disposition_notes: this.dispNotes }, hms);
        if (r.data && r.data.success) {
          this.showToast("Disposition: " + this.dispLabel);
          this.dispCode = null;
          this.active = null;
          await this.load();
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    }
  }
};
</script>
